<template>
  <footer class="footer">
    <!-- Decorative Wave -->
    <div class="wave-decoration">
      <svg viewBox="0 0 1200 120" preserveAspectRatio="none">
        <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" fill="currentColor"></path>
      </svg>
    </div>

    <div class="footer-main">
      <div class="container">
        <div class="footer-content">
          <!-- Brand Section -->
          <div class="footer-brand">
            <div class="brand-logo">
              <i class="fas fa-music"></i>
            </div>
            <h3 class="brand-name">{{ tr('footer.brandName') }}</h3>
            <p class="brand-tagline">{{ tr('footer.brandTagline') }}</p>
          </div>

          <!-- Navigation Links -->
          <nav class="footer-nav" aria-label="Footer navigation">
            <h4 class="footer-section-title">{{ tr('footer.navigation') }}</h4>
            <ul class="footer-nav-list">
              <li>
                <router-link to="/" class="footer-link" @click="trackNavClick('home')">
                  <i class="fas fa-home"></i>
                  {{ $t('nav.home') }}
                </router-link>
              </li>
              <li>
                <router-link to="/about" class="footer-link" @click="trackNavClick('about')">
                  <i class="fas fa-users"></i>
                  {{ $t('nav.about') }}
                </router-link>
              </li>
              <li>
                <router-link to="/school" class="footer-link" @click="trackNavClick('school')">
                  <i class="fas fa-graduation-cap"></i>
                  {{ tr('footer.schoolLink') }}
                </router-link>
              </li>
              <li>
                <router-link to="/events" class="footer-link" @click="trackNavClick('events')">
                  <i class="fas fa-calendar-alt"></i>
                  {{ $t('nav.events') }}
                </router-link>
              </li>
              <li>
                <router-link to="/contact" class="footer-link" @click="trackNavClick('contact')">
                  <i class="fas fa-envelope"></i>
                  {{ $t('nav.contact') }}
                </router-link>
              </li>
            </ul>
          </nav>

          <!-- Social Media -->
          <div class="footer-social">
            <h4 class="footer-section-title">{{ tr('footer.followUs') }}</h4>
            <div class="social-links">
              <a
                v-for="social in socialLinks"
                :key="social.name"
                :href="social.url"
                :aria-label="social.label"
                class="social-link"
                target="_blank"
                rel="noopener noreferrer"
                @click="trackSocialClick(social.name)"
              >
                <i :class="social.icon"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer Bottom -->
    <div class="footer-bottom">
      <div class="container">
        <div class="footer-bottom-content">
          <div class="footer-bottom-left">
            <p class="copyright">
              © {{ currentYear }} Banda de Música de Noia. {{ $t('footer.rights') }}
            </p>
            <div class="legal-links">
              <router-link to="/legal" class="legal-link">
                {{ $t('footer.legal') }}
              </router-link>
              <span class="separator">•</span>
              <router-link to="/privacy" class="legal-link">
                {{ $t('footer.privacy') }}
              </router-link>
              <span class="separator">•</span>
              <router-link to="/cookies" class="legal-link">
                {{ $t('footer.cookies') }}
              </router-link>
            </div>
          </div>
          <!-- Logos Section -->
          <div class="footer-logos">
            <div class="footer-logo-item">
              <img
                :src="bandaLogo"
                alt="Banda de Música de Noia"
                class="banda-logo"
                loading="lazy"
                decoding="async"
                width="220"
                height="120"
              />
            </div>
            <div class="footer-logo-item">
              <a
                href="https://www.dacoruna.gal/"
                class="footer-logo-link"
                aria-label="Diputación de A Coruña"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  :src="diputacionLogo"
                  alt="Diputación de A Coruña"
                  class="sponsor-logo"
                  loading="lazy"
                  decoding="async"
                  width="220"
                  height="80"
                />
              </a>
            </div>
            <div class="footer-logo-item">
              <a
                href="https://concellodenoia.gal/"
                class="footer-logo-link"
                aria-label="Concello de Noia"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  :src="concelloNoiaLogo"
                  alt="Concello de Noia"
                  class="concello-logo"
                  loading="lazy"
                  decoding="async"
                  width="80"
                  height="96"
                />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance } from 'vue';
import { useAnalytics } from '../../composables/useAnalytics'
import diputacionLogo from '../../assets/DepCor_hor_Negativo_fondo.svg'
import bandaLogo from '../../assets/24_07_2025_LogoBandaNuevo.jpg'
import concelloNoiaLogo from '../../assets/Escudo_do_concello_de_Noia.png'

const emit = defineEmits<{
  'show-cookies': [];
}>();

// Access i18n from current instance
const instance = getCurrentInstance();
const $t = instance?.appContext.config.globalProperties.$t;
const tr = (key: string) => ($t ? $t(key) : key);

// Analytics
const { trackEvent, trackLinkClick } = useAnalytics()

const currentYear = computed(() => new Date().getFullYear());

const socialLinks = [
  {
    name: 'facebook',
    url: 'https://www.facebook.com/bandademusicadenoia',
    label: 'Facebook',
    icon: 'fab fa-facebook-f',
  },
  {
    name: 'instagram',
    url: 'https://www.instagram.com/bandadenoia',
    label: 'Instagram',
    icon: 'fab fa-instagram',
  },
  {
    name: 'youtube',
    url: 'https://www.youtube.com/@bandademusicadenoia',
    label: 'YouTube',
    icon: 'fab fa-youtube',
  },
  {
    name: 'twitter',
    url: 'https://x.com/bandadenoia',
    label: 'Twitter / X',
    icon: 'fab fa-x-twitter',
  },
  {
    name: 'tiktok',
    url: 'https://www.tiktok.com/@bandadenoia',
    label: 'TikTok',
    icon: 'fab fa-tiktok',
  },
];

const handleShowCookies = () => {
  emit('show-cookies');
};

// Track navigation clicks
const trackNavClick = (page: string) => {
  trackEvent('footer_nav_click', 'navigation', page)
}

// Track social clicks
const trackSocialClick = (platform: string) => {
  trackLinkClick(platform, socialLinks.find(s => s.name === platform)?.url || '')
  trackEvent('footer_social_click', 'engagement', platform)
}
</script>

<style scoped>
.footer {
  position: relative;
  background: var(--gradient-primary);
  color: white;
  overflow: hidden;
}

.wave-decoration {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 80px;
  transform: translateY(-99%);
  color: var(--color-primary-900);
  z-index: 1;
}

.wave-decoration svg {
  width: 100%;
  height: 100%;
  display: block;
}

.footer-main {
  padding: var(--spacing-16) 0 var(--spacing-12) 0;
}

.container {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 var(--spacing-6);
}

.footer-content {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--spacing-12);
}

@media (min-width: 768px) {
  .footer-content {
    grid-template-columns: 2fr 1.2fr 1fr;
    gap: var(--spacing-16);
  }
}

/* Brand Section */
.footer-brand {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 28rem;
}

@media (min-width: 768px) {
  .footer-brand {
    align-items: flex-start;
    text-align: left;
  }
}

.brand-logo {
  width: 70px;
  height: 70px;
  background: var(--gradient-secondary);
  border-radius: var(--border-radius-2xl);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  margin-bottom: var(--spacing-4);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  transition: transform var(--duration-normal);
}

.brand-logo:hover {
  transform: translateY(-2px);
}

.brand-name {
  font-family: var(--font-family-display);
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  margin: 0 0 var(--spacing-2) 0;
  letter-spacing: 0.03em;
  line-height: 1.12;
  color: #ffffff;
  text-wrap: balance;
  text-shadow: 0 6px 18px rgba(0, 0, 0, 0.35);
}

.brand-tagline {
  font-family: var(--font-family-serif);
  font-size: var(--font-size-base);
  color: rgba(255, 255, 255, 0.95);
  margin: 0;
  font-style: italic;
  text-shadow: 0 3px 10px rgba(0, 0, 0, 0.25);
}

/* Section Titles */
.footer-section-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  margin: 0 0 var(--spacing-5) 0;
  color: var(--color-secondary-200);
  font-family: var(--font-family-serif);
  letter-spacing: 0.5px;
  text-transform: uppercase;
  font-size: var(--font-size-sm);
}

/* Navigation */
.footer-nav {
  display: flex;
  flex-direction: column;
}

.footer-nav-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-3);
}

.footer-link {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-2);
  color: rgba(255, 255, 255, 0.85);
  text-decoration: none;
  font-size: var(--font-size-base);
  transition: all var(--duration-fast);
  padding: var(--spacing-1) 0;
}

.footer-link i {
  font-size: var(--font-size-sm);
  width: 18px;
  color: var(--color-secondary-300);
  transition: all var(--duration-fast);
}

.footer-link:hover {
  color: white;
  transform: translateX(4px);
}

.footer-link:hover i {
  color: var(--color-secondary-200);
}

/* Social Media */
.footer-social {
  display: flex;
  flex-direction: column;
}

.social-links {
  display: flex;
  gap: var(--spacing-3);
  flex-wrap: wrap;
}

.social-link {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.12);
  border: 2px solid rgba(255, 255, 255, 0.25);
  border-radius: var(--border-radius-xl);
  color: white;
  font-size: var(--font-size-xl);
  text-decoration: none;
  transition: all var(--duration-normal);
  backdrop-filter: blur(4px);
}

.social-link:hover {
  background: rgba(255, 255, 255, 0.25);
  border-color: rgba(255, 255, 255, 0.4);
  transform: translateY(-4px) scale(1.05);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.25);
}

.social-link:nth-child(1):hover {
  background: #1877f2;
  border-color: #1877f2;
}

.social-link:nth-child(2):hover {
  background: linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%);
  border-color: #bc1888;
}

.social-link:nth-child(3):hover {
  background: #ff0000;
  border-color: #ff0000;
}

.social-link:nth-child(4):hover {
  background: #000000;
  border-color: #000000;
}

.social-link:nth-child(5):hover {
  background: #000000;
  border-color: #000000;
}

/* Footer Bottom */
.footer-bottom {
  border-top: 1px solid rgba(255, 255, 255, 0.15);
  padding: var(--spacing-6) 0;
  background: rgba(0, 0, 0, 0.15);
}

.footer-bottom-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-6);
  text-align: center;
}

@media (min-width: 768px) {
  .footer-bottom-content {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    text-align: left;
    gap: var(--spacing-8);
  }
}

.footer-bottom-left {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-4);
}

@media (min-width: 768px) {
  .footer-bottom-left {
    align-items: flex-start;
  }
}

.copyright {
  margin: 0;
  font-size: var(--font-size-sm);
  color: rgba(255, 255, 255, 0.7);
  font-weight: var(--font-weight-normal);
}

.legal-links {
  display: flex;
  align-items: center;
  gap: var(--spacing-3);
  font-size: var(--font-size-sm);
  flex-wrap: wrap;
  justify-content: center;
}

.legal-link {
  color: rgba(255, 255, 255, 0.75);
  text-decoration: none;
  background: none;
  border: none;
  cursor: pointer;
  font-family: inherit;
  font-size: inherit;
  padding: var(--spacing-1) 0;
  transition: all var(--duration-fast);
  font-weight: var(--font-weight-medium);
}

.legal-link:hover {
  color: var(--color-secondary-200);
  text-decoration: underline;
}

.separator {
  color: rgba(255, 255, 255, 0.4);
  user-select: none;
}

/* Logos Section */
.footer-logos {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  gap: var(--spacing-6);
  flex-wrap: wrap;
}

@media (min-width: 768px) {
  .footer-logos {
    margin-left: auto;
    gap: var(--spacing-8);
  }
}

.footer-logo-item {
  display: flex;
  align-items: center;
  justify-content: center;
}

.footer-logo-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  border-radius: var(--border-radius-lg);
  transition: transform var(--duration-fast);
}

.footer-logo-link:hover {
  transform: translateY(-2px);
}

.banda-logo {
  height: 60px;
  width: auto;
  max-width: 200px;
  object-fit: contain;
  opacity: 0.9;
  transition: all var(--duration-fast);
  border-radius: var(--border-radius-lg);
}

.banda-logo:hover {
  opacity: 1;
  transform: scale(1.02);
}

.sponsor-logo {
  height: 60px;
  width: 180px;
  object-fit: contain;
  transition: all 0.2s ease;
  display: block;
}

.concello-logo {
  height: 68px;
  width: auto;
  max-width: 72px;
  object-fit: contain;
  transition: all 0.2s ease;
  display: block;
}

@media (min-width: 768px) {
  .banda-logo {
    height: 70px;
    max-width: 220px;
  }
  
  .sponsor-logo {
    height: 70px;
    width: 220px;
  }

  .concello-logo {
    height: 78px;
    max-width: 82px;
  }
}

/* Responsive adjustments */
@media (max-width: 767px) {
  .footer-content {
    text-align: center;
  }

  .footer-nav,
  .footer-social {
    align-items: center;
  }

  .footer-nav-list {
    align-items: center;
  }

  .social-links {
    justify-content: center;
  }
}
</style>
